import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react'
import { Vega } from 'react-vega'

import { api, type MatrixResult } from '../lib/api'
import { RADAR_HEIGHT, RADAR_WIDTH, buildRadarSpec, pickWedge, radarRows } from '../lib/radarSpec'

interface Props {
  analysisId: string
  result: MatrixResult
  /** Ausente para quem só lê: os controles aparecem, desabilitados. */
  onSaved?: () => void
}

/**
 * Peso por pasta do Profiles, aplicado só no score ajustado.
 *
 * A afinidade diz o que distingue a audiência, não o que importa para a marca —
 * "Saúde" é distintivo para 25+ e irrelevante para biscoito. Esse julgamento é de
 * quem planeja, então entra como multiplicador gravado em `overrides.json`, e o
 * ranking reordena sem recálculo.
 */
export function FolderWeights({ analysisId, result, onSaved }: Props) {
  const [weights, setWeights] = useState<Record<string, number>>({})
  const [saved, setSaved] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dragging = useRef(false)
  const rows = useMemo(() => radarRows(result.points, weights), [result, weights])
  const spec = useMemo(() => buildRadarSpec(rows.length), [rows.length])

  useEffect(() => {
    api.folderWeights(analysisId).then((current) => {
      setWeights(current)
      setSaved(current)
    }).catch((exc: Error) => setError(exc.message))
  }, [analysisId])

  if (rows.length === 0) {
    return (
      <section className="folder-weights">
        <p className="eyebrow">Relevância por dimensão</p>
        <p>Esta análise é anterior às pastas do Profiles. Recalcule-a para ajustar pesos por dimensão.</p>
      </section>
    )
  }

  const editable = Boolean(onSaved) && !saving
  const pick = (event: PointerEvent<HTMLDivElement>) => {
    // O SVG pode estar encolhido pelo CSS em tela estreita; a conta é no tamanho nominal.
    const box = event.currentTarget.getBoundingClientRect()
    const hit = pickWedge(
      ((event.clientX - box.left) * RADAR_WIDTH) / box.width,
      ((event.clientY - box.top) * RADAR_HEIGHT) / box.height,
      rows.length,
    )
    if (hit) setWeights((current) => ({ ...current, [rows[hit.index].folder]: hit.weight }))
  }

  const dirty = JSON.stringify(weights) !== JSON.stringify(saved)
  const save = () => {
    setSaving(true)
    setError(null)
    // Peso 1 é o neutro: só o que foi rebaixado vai para o override.
    const kept = Object.fromEntries(Object.entries(weights).filter(([, w]) => w < 1))
    api.setFolderWeights(analysisId, kept)
      .then(() => {
        setSaved(kept)
        setWeights(kept)
        onSaved?.()
      })
      .catch((exc: Error) => setError(exc.message))
      .finally(() => setSaving(false))
  }

  return (
    <section className="folder-weights" aria-label="Relevância por dimensão">
      <div>
        <p className="eyebrow">Relevância por dimensão</p>
        <p>
          Quanto cada pasta do Profiles importa para esta marca. Clique ou arraste dentro
          da fatia: o centro é 0, a borda é 1. Multiplica o score ajustado; o bruto não
          muda. Fatia cinza não tem território no ranking — o peso dela não move nada.
        </p>
      </div>
      <div
        className="folder-radar"
        style={{ touchAction: editable ? 'none' : undefined }}
        onPointerDown={editable ? (event) => {
          dragging.current = true
          event.currentTarget.setPointerCapture(event.pointerId)
          pick(event)
        } : undefined}
        onPointerMove={editable ? (event) => dragging.current && pick(event) : undefined}
        onPointerUp={() => { dragging.current = false }}
      >
        <Vega spec={spec} data={{ folders: rows }} actions={false} renderer="svg" />
      </div>
      <ul className="folder-radar-list">
        {rows.map((row) => (
          <li key={row.folder} className={row.ranked === 0 ? 'inert' : undefined}>
            <span>{row.folder}</span>
            <output>{row.weight.toFixed(1)}</output>
          </li>
        ))}
      </ul>
      {onSaved && (
        <div className="folder-weights-actions">
          <button type="button" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Aplicando…' : 'Aplicar pesos'}
          </button>
          <button type="button" disabled={!dirty || saving} onClick={() => setWeights(saved)}>
            Desfazer
          </button>
        </div>
      )}
      {error && <p className="brief-error">{error}</p>}
    </section>
  )
}
